// The stereonet canvas: density raster and contours, poles, set windows, mean planes,
// gm / bm, the rosette overlay; pointer readout; drawing and editing set windows.
// Ported from DipField 1.0.0 (kinematic and tunnel layers go to their modules).
// GPL-3.0-or-later.
import * as S from "@dipfield/stereo";
import { t } from "../../i18n/index.js";
import { $ } from "../../lib/dom.js";
import { fmt, pad2, pad3, fmtPlane, fmtName } from "../../lib/format.js";
import { css, ramp, hexToRgb, setColor } from "../../lib/theme.js";
import * as M from "../../lib/motion.js";
import { pushHistory, dropHistory } from "../../state/store.js";
import { addSet } from "../../state/ops.js";
import { weighted } from "../../state/derived.js";
import { X, P, on, update, draw as drawAll, onDraw, onRecompute, type Tool } from "./ctx.js";
import { rosePetalsNet } from "./rose.js";

let cv: HTMLCanvasElement, ctx: CanvasRenderingContext2D;
let R = 300, cx = 320, cy = 320, dpr = 1, cssSize = 640;
let rasterKey = "", raster: HTMLCanvasElement | null = null, prevRaster: HTMLCanvasElement | null = null;
const anim = { net: 1, poles: 1, contour: 1 };
let lastPoleCount = -1;
const REVEAL_LIMIT = 3000; // past this many poles the stagger is dropped, never the data
let labelBoxes: { x0: number; y0: number; x1: number; y1: number }[] = [];
let hover: { px: number; py: number } | null = null;
let draft: S.SetWindow | null = null, dragStart: S.Vec3 | null = null;
let curvedStart: { trend: number; plunge: number } | null = null;
let edit: { k: number; mode: "move" | "resize"; start: S.Vec3; orig: S.SetWindow; moved: boolean } | null = null;

export const toolHint = (k: Tool) => ({
  view: t("Hover the net to read values"),
  circle: t("Press at the set centre and drag outwards to set the radius; drag past the rim if the set straddles it"),
  curved: t("Drag from one corner of the trend/plunge range to the other; drag past the rim to include poles on the far side"),
  freehand: t("Press and draw around a group of poles; release when done"),
  edit: t("Click inside a window to select it, drag to move, drag the circle edge to resize, or type values in the set card"),
}[k]);

function play(key: "net" | "poles", ms: number, ease = M.easeOut) {
  anim[key] = 0;
  M.tween("net:" + key, ms, (v) => { anim[key] = v; drawAll(); }, ease, () => { anim[key] = 1; drawAll(); });
}
/** Pole reveal when rows were added. */
export function poleCountChanged() {
  const n = P().data.planes.length;
  if (n !== lastPoleCount) {
    const grew = n > lastPoleCount; lastPoleCount = n;
    if (grew && n) play("poles", n > 600 ? 900 : 700);
  }
}
export const invalidateRaster = () => { rasterKey = ""; };

export function resize() {
  const box = $("netbox").getBoundingClientRect();
  cssSize = Math.max(260, Math.floor(Math.min(box.width, box.height || box.width)));
  dpr = Math.min(3, window.devicePixelRatio || 1);
  cv.style.width = cv.style.height = cssSize + "px";
  cv.width = cv.height = Math.round(cssSize * dpr);
  const m = Math.max(28, cssSize * 0.07);
  R = cssSize / 2 - m; cx = cy = cssSize / 2;
  rasterKey = "";
  drawAll();
}
const toPx = (q: S.XY) => [cx + q.x * R, cy - q.y * R] as const;
const toNet = (px: number, py: number): S.XY => ({ x: (px - cx) / R, y: (cy - py) / R });
function bandIndex(v: number) { let k = 0; while (k < X.levels.length && v >= X.levels[k]) k++; return k; }

/** The filled density bands (or the smooth ramp) as an image `size` px square. */
export function densityImage(size: number): HTMLCanvasElement | null {
  const grid = X.grid, levels = X.levels; if (!grid || !levels.length) return null;
  const smooth = P().stereo.display.contourStyle === "smooth";
  const c = document.createElement("canvas"); c.width = c.height = size;
  const g = c.getContext("2d")!; const img = g.createImageData(size, size);
  const cols = ramp().map(hexToRgb), nb = cols.length, l0 = levels[0], max = grid.max;
  for (let j = 0; j < size; j++) for (let i = 0; i < size; i++) {
    const x = (2 * (i + 0.5)) / size - 1, y = 1 - (2 * (j + 0.5)) / size;
    if (x * x + y * y > 1) continue;
    const v = S.sampleGrid(grid, x, y), o = 4 * (j * size + i);
    if (smooth) {
      // continuous ramp: the colour follows the value, the lowest band fades in from clear
      if (v <= l0 * 0.35) continue;
      const tt = Math.max(0, Math.min(1, (v - l0) / Math.max(1e-9, max - l0))) * (nb - 1), k = Math.min(nb - 2, Math.floor(tt)), f = tt - k;
      const a = cols[k], b = cols[k + 1], alpha = v < l0 ? (v - l0 * 0.35) / (l0 * 0.65) : 1;
      img.data[o] = a[0] + (b[0] - a[0]) * f; img.data[o + 1] = a[1] + (b[1] - a[1]) * f; img.data[o + 2] = a[2] + (b[2] - a[2]) * f; img.data[o + 3] = Math.round(255 * alpha);
    } else {
      const k = bandIndex(v);
      if (k === 0) continue;
      const col = cols[Math.min(nb - 1, k - 1)];
      img.data[o] = col[0]; img.data[o + 1] = col[1]; img.data[o + 2] = col[2]; img.data[o + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  return c;
}
function buildRaster(): HTMLCanvasElement | null {
  const p = P();
  const key = [cssSize, dpr, p.stereo.projection.method, p.stereo.projection.hemisphere, X.grid?.max, X.levels.join(","), p.stereo.display.contourStyle].join("|");
  if (key === rasterKey && raster) return raster;
  const c = densityImage(Math.round(2 * R * dpr)); if (!c) return null;
  // a new raster cross-fades over the old one; the very first one rises out of the paper.
  // The raster is in place before the tween starts, and with animation off there is no tween:
  // this runs inside draw(), and a tween with motion off calls back at once, so a redraw from
  // here would build the raster again, and again (the page froze, animation off, after the
  // rosette and back).
  const prev = raster && rasterKey ? raster : null;
  raster = c; rasterKey = key;
  if (!M.fxOn()) { M.cancel("net:contour"); prevRaster = null; anim.contour = 1; return c; }
  prevRaster = prev; anim.contour = 0;
  M.tween("net:contour", prev ? 280 : 650, (v) => { anim.contour = v; drawAll(); }, M.easeInOut, () => { anim.contour = 1; prevRaster = null; });
  return c;
}

// Labels on the net fight each other as soon as there are a few sets, so every label
// is placed in the first free slot around its anchor and skipped when nothing is free.
function drawLabel(text: string, x: number, y: number, color: string, size = 13, weight = 600) {
  const halo = css("--net-bg");
  ctx.font = `${weight} ${size}px ${css("--font-ui")}`;
  const w = ctx.measureText(text).width, h = size * 1.05, d = size * 0.55;
  const slots: [number, number, CanvasTextAlign, CanvasTextBaseline][] = [
    [x + d, y - d, "left", "bottom"], [x + d, y + d, "left", "top"],
    [x - d, y - d, "right", "bottom"], [x - d, y + d, "right", "top"],
    [x + d + 2, y, "left", "middle"], [x - d - 2, y, "right", "middle"],
    [x, y - d - 4, "center", "bottom"], [x, y + d + 4, "center", "top"],
  ];
  const boxed = slots.map(([lx, ly, al, bl]) => {
    const x0 = al === "left" ? lx : al === "right" ? lx - w : lx - w / 2;
    const y0 = bl === "middle" ? ly - h / 2 : bl === "bottom" ? ly - h : ly;
    const box = { x0: x0 - 2, y0: y0 - 1, x1: x0 + w + 2, y1: y0 + h + 1 };
    const far = Math.max(Math.hypot(box.x0 - cx, box.y0 - cy), Math.hypot(box.x1 - cx, box.y0 - cy), Math.hypot(box.x0 - cx, box.y1 - cy), Math.hypot(box.x1 - cx, box.y1 - cy));
    return { lx, ly, al, bl, box, far };
  });
  // near the rim, try the slots that point back into the net first
  const rimClose = Math.hypot(x - cx, y - cy) > R * 0.8;
  if (rimClose) boxed.sort((a, b) => a.far - b.far);
  for (const { lx, ly, al, bl, box, far } of boxed) {
    if (far > R + 2 && rimClose) continue;  // would be clipped by the primitive
    if (labelBoxes.some((r) => box.x0 < r.x1 && box.x1 > r.x0 && box.y0 < r.y1 && box.y1 > r.y0)) continue;
    labelBoxes.push(box);
    ctx.textAlign = al; ctx.textBaseline = bl;
    ctx.lineJoin = "round"; ctx.lineWidth = 3.4; ctx.strokeStyle = halo; ctx.strokeText(text, lx, ly);
    ctx.fillStyle = color; ctx.fillText(text, lx, ly);
    return true;
  }
  return false;
}

function draw() {
  const p = P(), st = p.stereo, disp = st.display, proj = st.projection;
  const ink = css("--ink"), ink2 = css("--ink-2"), hair = css("--hair"), paper = css("--net-bg");
  labelBoxes = [];
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssSize, cssSize);
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, 2 * Math.PI); ctx.fillStyle = paper; ctx.fill();
  ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, R, 0, 2 * Math.PI); ctx.clip();
  if (disp.contours) {
    const r = buildRaster();
    if (prevRaster && anim.contour < 1) { ctx.globalAlpha = 1 - anim.contour; ctx.drawImage(prevRaster, cx - R, cy - R, 2 * R, 2 * R); }
    if (r) { ctx.globalAlpha = anim.contour; ctx.drawImage(r, cx - R, cy - R, 2 * R, 2 * R); }
    ctx.globalAlpha = 1;
    if (X.grid) {
      ctx.strokeStyle = css("--contour-line"); ctx.lineWidth = 0.7; ctx.beginPath();
      for (const lv of X.levels) for (const [a, b] of S.contourSegments(X.grid, lv)) {
        const [ax, ay] = toPx(a), [bx, by] = toPx(b); ctx.moveTo(ax, ay); ctx.lineTo(bx, by);
      }
      ctx.stroke();
    }
  }
  // rosette over the stereonet
  const rp = rosePetalsNet();
  if (rp) {
    const path = (q: S.XY[]) => { ctx.beginPath(); q.forEach((v, i) => { const [x, y] = toPx(v); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); }); ctx.closePath(); };
    ctx.fillStyle = rp.color; ctx.strokeStyle = rp.color; ctx.lineWidth = 1; ctx.globalAlpha = 0.25;
    for (const q of rp.pts) { path(q); ctx.fill(); }
    ctx.globalAlpha = 0.8; for (const q of rp.pts) { path(q); ctx.stroke(); }
    ctx.globalAlpha = 1;
    if (rp.mean) { ctx.setLineDash([6, 4]); ctx.strokeStyle = ink; ctx.lineWidth = 1.4; ctx.beginPath();
      const [x1, y1] = toPx(rp.mean[0]), [x2, y2] = toPx(rp.mean[1]); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); ctx.setLineDash([]); }
  }
  // overlay grid
  const grid = (kind: S.OverlayKind, color: string, alpha: number) => {
    ctx.globalAlpha = alpha; ctx.strokeStyle = color; ctx.lineWidth = 0.7; ctx.beginPath();
    for (const line of S.overlayGrid(kind, proj)) line.forEach((q, i) => { const [x, y] = toPx(q); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
    ctx.stroke(); ctx.globalAlpha = 1;
  };
  if (disp.overlay !== "none") grid(disp.overlay, css("--grid"), 1);
  // with no data the net would be one empty ring, so sketch the graticule in faintly
  else if (!p.data.planes.length) grid("polar", hair, 0.55);
  // centre cross
  ctx.strokeStyle = hair; ctx.lineWidth = 1; ctx.beginPath();
  ctx.moveTo(cx - 6, cy); ctx.lineTo(cx + 6, cy); ctx.moveTo(cx, cy - 6); ctx.lineTo(cx, cy + 6); ctx.stroke();
  // poles
  if (disp.poles) {
    const r = disp.poleSize, anySets = p.sets.length > 0;
    const ap = anim.poles, live = ap < 1 && X.polePts.length <= REVEAL_LIMIT;
    X.polePts.forEach((q, i) => {
      const a = X.d.assignment[i];
      const inSet = X.hoverSet < 0 || a === X.hoverSet + 1;
      ctx.globalAlpha = inSet ? 1 : 0.18;
      ctx.fillStyle = !anySets ? ink : a ? setColor(a - 1) : ink2;
      const [x, y] = toPx(q);
      if (X.hoverSet >= 0 && inSet && a) { ctx.beginPath(); ctx.arc(x, y, r * 1.5, 0, 2 * Math.PI); ctx.fill(); return; }
      if (live) {
        // stagger by distance from the centre, so the reveal reads as a wave running outwards
        const dd = Math.min(1, Math.hypot(x - cx, y - cy) / Math.max(1, R));
        const tt = M.clamp01((ap - dd * 0.45) / 0.55);
        if (tt <= 0) return;
        ctx.globalAlpha = tt;
        ctx.beginPath(); ctx.arc(x, y, r * (0.4 + 0.6 * M.easeOutBack(tt)), 0, 2 * Math.PI); ctx.fill();
      } else { ctx.beginPath(); ctx.arc(x, y, r, 0, 2 * Math.PI); ctx.fill(); }
    });
    ctx.globalAlpha = 1;
  }
  // set windows, mean set planes, cones
  const poly = (pts: S.XY[]) => { ctx.beginPath(); pts.forEach((q, i) => { const [x, y] = toPx(q); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); }); ctx.stroke(); };
  p.sets.forEach((set, k) => {
    const col = setColor(k), lit = k === X.selectedSet || k === X.hoverSet;
    ctx.strokeStyle = col; ctx.lineWidth = lit ? 2.6 : 1.3; ctx.setLineDash(lit ? [] : [5, 4]);
    S.windowOutline(set.window, proj).forEach(poly); ctx.setLineDash([]);
    const sm = X.d.summaries[k];
    if (!sm?.stats || !sm.meanPlane) return;
    if (disp.setPlanes) { ctx.lineWidth = 1.8; poly(S.greatCircle(sm.meanPlane.dip, sm.meanPlane.dipDir, proj)); }
    if (disp.cones && sm.stats.confidenceDeg) { ctx.lineWidth = 1.4; S.smallCircle(sm.stats.mean, Math.max(0.3, sm.stats.confidenceDeg), proj).forEach(poly); }
    const [mx, my] = toPx(S.project(sm.stats.mean, proj));
    ctx.strokeStyle = paper; ctx.lineWidth = 2.5; ctx.strokeRect(mx - 4, my - 4, 8, 8);
    ctx.fillStyle = col; ctx.fillRect(mx - 4, my - 4, 8, 8);
    labelBoxes.push({ x0: mx - 6, y0: my - 6, x1: mx + 6, y1: my + 6 });
    drawLabel(set.name, mx, my, col, 13);
  });
  if (draft) {
    ctx.strokeStyle = css("--accent"); ctx.lineWidth = 1.6; ctx.setLineDash([3, 3]);
    if (draft.kind === "freehand" && draft.vertices.length < 3) poly(draft.vertices.map((v) => S.projectExt(v, proj)));
    else S.windowOutline(draft, proj).forEach(poly);
    ctx.setLineDash([]);
  }
  // global mean (gm) and global best fit (bm), as in DIPS
  const planeAndPole = (v: S.Vec3, color: string, label: string) => {
    const pl = S.poleToPlane(v);
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 1.6; ctx.beginPath();
    S.greatCircle(pl.dip, pl.dipDir, proj).forEach((q, i) => { const [x, y] = toPx(q); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
    ctx.stroke();
    const [mx, my] = toPx(S.project(v, proj));
    ctx.lineWidth = 1.6; ctx.beginPath(); ctx.moveTo(mx - 6, my); ctx.lineTo(mx + 6, my); ctx.moveTo(mx, my - 6); ctx.lineTo(mx, my + 6); ctx.stroke();
    drawLabel(label, mx, my, color, 13);
  };
  if (disp.globalMean && X.d.gm) planeAndPole(X.d.gm, css("--gm"), "gm");
  if (disp.bestFit && X.d.bm) planeAndPole(X.d.bm, css("--bm"), "bm");
  if (X.hoverRow >= 0 && X.hoverRow < X.polePts.length) {
    const [x, y] = toPx(X.polePts[X.hoverRow]), acc = css("--accent");
    ctx.beginPath(); ctx.arc(x, y, disp.poleSize + 7, 0, 2 * Math.PI); ctx.strokeStyle = paper; ctx.lineWidth = 4; ctx.stroke();
    ctx.beginPath(); ctx.arc(x, y, disp.poleSize + 7, 0, 2 * Math.PI); ctx.strokeStyle = acc; ctx.lineWidth = 2.2; ctx.stroke();
    ctx.beginPath(); ctx.arc(x, y, disp.poleSize + 1.5, 0, 2 * Math.PI); ctx.fillStyle = acc; ctx.fill();
    drawLabel(t("Row {n}", { n: X.hoverRow + 1 }), x, y, acc, 12, 700);
  }
  ctx.restore();
  // primitive + ticks every 10°, labels at N/E/S/W — drawn on with the net reveal
  const an = anim.net, top = -Math.PI / 2;
  ctx.strokeStyle = ink; ctx.lineWidth = 1.4;
  ctx.beginPath(); ctx.arc(cx, cy, R, top, top + 2 * Math.PI * an); ctx.stroke();
  ctx.lineWidth = 1; ctx.strokeStyle = ink2; ctx.beginPath();
  for (let a = 0; a < 360; a += 10) {
    if (a / 360 > an) continue;
    const tt = a * S.DEG, len = a % 90 === 0 ? 10 : a % 30 === 0 ? 6 : 3.5;
    ctx.moveTo(cx + Math.sin(tt) * R, cy - Math.cos(tt) * R);
    ctx.lineTo(cx + Math.sin(tt) * (R + len), cy - Math.cos(tt) * (R + len));
  }
  ctx.stroke();
  ctx.globalAlpha = M.clamp01((an - 0.6) / 0.4);
  ctx.fillStyle = ink; ctx.font = `600 ${Math.max(12, cssSize * 0.024)}px ${css("--font-ui")}`;
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  const off = R + Math.max(20, cssSize * 0.04);
  ctx.fillText("N", cx, cy - off); ctx.fillText("S", cx, cy + off);
  ctx.fillText("E", cx + off, cy); ctx.fillText("W", cx - off, cy);
  ctx.globalAlpha = 1;
  $("empty").hidden = p.data.planes.length > 0;
  drawHover();
}

// ---------- hover / readout ----------
function nearestPole(px: number, py: number) {
  let best = -1, bd = 64;
  X.polePts.forEach((q, i) => { const [x, y] = toPx(q); const d = (x - px) ** 2 + (y - py) ** 2; if (d < bd) { bd = d; best = i; } });
  return best;
}
function linkHighlight(i: number) {
  document.querySelectorAll(".hl").forEach((e) => e.classList.remove("hl"));
  if (i < 0) return;
  $("tbody").querySelector(`tr[data-i="${i}"]`)?.classList.add("hl");
  const a = X.d.assignment[i]; if (a) document.querySelector(`.setcard[data-k="${a - 1}"]`)?.classList.add("hl");
}
function drawHover() {
  const read = $("readout"), tip = $("tip"), p = P(), proj = p.stereo.projection, f = p.data.format;
  if (!hover) { tip.hidden = true; linkHighlight(-1); read.textContent = toolHint(X.tool); return; }
  const n = toNet(hover.px, hover.py);
  if (n.x * n.x + n.y * n.y > 1) { tip.hidden = true; read.textContent = toolHint("view"); return; }
  const v = S.unproject(n, proj), ln = S.vecToLine(v), pl = S.poleToPlane(v);
  read.textContent = t("This point is the pole of plane {plane} ({fmt}) · pole trend/plunge {tp}", { plane: fmtPlane(pl.dip, pl.dipDir, f), fmt: fmtName(f), tp: `${pad3(ln.trend)}/${pad2(ln.plunge)}` }) +
    (X.grid ? " · " + t("density {d}%", { d: fmt(S.sampleGrid(X.grid, n.x, n.y), 2) }) : "");
  const i = p.stereo.display.poles ? nearestPole(hover.px, hover.py) : -1;
  linkHighlight(i);
  if (i < 0) { tip.hidden = true; return; }
  const q = p.data.planes[i], [x, y] = toPx(X.polePts[i]), a = X.d.assignment[i];
  tip.hidden = false;
  tip.textContent = t("Row {n}", { n: i + 1 }) + `: ${fmtPlane(q.dip, q.dipDir, f)}` + (a ? ` · ${p.sets[a - 1].name}` : "") +
    (weighted(p) ? ` · w ${fmt(X.d.tWeights[i], 2)}` : "");
  tip.style.left = x + "px"; tip.style.top = y + "px";
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.strokeStyle = css("--accent"); ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(x, y, p.stereo.display.poleSize + 4, 0, 2 * Math.PI); ctx.stroke();
}

// ---------- set window tools ----------
const evNet = (e: PointerEvent) => { const r = cv.getBoundingClientRect(); return toNet(e.clientX - r.left, e.clientY - r.top); };
/** Line (trend, plunge) of a pointer position; beyond the rim gives negative plunge (wrap). */
function extLine(pt: S.XY) {
  const proj = P().stereo.projection;
  let v = S.unprojectExt(pt, proj);
  if (proj.hemisphere === "upper") v = [-v[0], -v[1], -v[2]];
  return { trend: S.wrap360(Math.atan2(v[0], v[1]) / S.DEG), plunge: Math.asin(Math.max(-1, Math.min(1, -v[2]))) / S.DEG };
}
function curvedFrom(a: { trend: number; plunge: number }, b: { trend: number; plunge: number }): S.CurvedWindow {
  const d = S.wrap360(b.trend - a.trend);
  const [start, width] = d <= 180 ? [a.trend, d] : [b.trend, 360 - d];
  const lo = Math.max(-30, Math.min(a.plunge, b.plunge)), hi = Math.min(90, Math.max(a.plunge, b.plunge));
  return { kind: "curved", trendStart: start, trendWidth: width, plungeMin: lo, plungeMax: hi };
}
function pickSet(v: S.Vec3): { k: number; mode: "move" | "resize" } | null {
  // prefer the selected set, then the most recently drawn
  const sets = P().sets, order = sets.map((_, k) => k).reverse();
  if (X.selectedSet >= 0) order.unshift(X.selectedSet);
  for (const k of order) {
    const w = sets[k]?.window; if (!w) continue;
    if (w.kind === "circle") {
      const d = S.axialAngle(v, w.axis);
      if (Math.abs(d - w.radius) <= Math.max(1.5, w.radius * 0.12)) return { k, mode: "resize" };
      if (d < w.radius) return { k, mode: "move" };
    } else if (S.windowContains(w, v)) return { k, mode: "move" };
  }
  return null;
}
const clampRim = (n: S.XY) => { const rr = Math.hypot(n.x, n.y); return rr > 1.3 ? { x: (n.x * 1.3) / rr, y: (n.y * 1.3) / rr } : n; };

export function setTool(k: Tool) {
  X.tool = k;
  cv.style.cursor = k === "view" ? "crosshair" : k === "edit" ? "move" : "cell";
  $("tool-hint").textContent = toolHint(k);
  const r = document.querySelector<HTMLInputElement>(`input[name="tool"][value="${k}"]`); if (r) r.checked = true;
  drawAll();
}
export function cancelDraft() { draft = null; dragStart = null; curvedStart = null; }

/** Selection of a set changed elsewhere (set card): re-render handled by the caller. */
export function mountNet(renderSets: () => void) {
  cv = $<HTMLCanvasElement>("net"); ctx = cv.getContext("2d")!;
  rasterKey = ""; raster = prevRaster = null; lastPoleCount = P().data.planes.length; hover = null; cancelDraft(); edit = null;
  onDraw(draw);
  onRecompute(() => { X.polePts = X.d.poles.map((v) => S.project(v, P().stereo.projection)); });
  X.polePts = X.d.poles.map((v) => S.project(v, P().stereo.projection));

  on(cv, "pointerdown", (e: PointerEvent) => {
    const proj = P().stereo.projection;
    if (X.tool === "edit") {
      const n = evNet(e); if (Math.hypot(n.x, n.y) > 1.3) return;
      const v = S.unprojectExt(n, proj), hit = pickSet(v);
      X.selectedSet = hit ? hit.k : -1;
      if (hit) { cv.setPointerCapture(e.pointerId); e.preventDefault(); pushHistory();
        edit = { ...hit, start: v, orig: JSON.parse(JSON.stringify(P().sets[hit.k].window)), moved: false }; }
      renderSets(); drawAll(); return;
    }
    if (X.tool === "view") return;
    const n = evNet(e); if (Math.hypot(n.x, n.y) > 1.02) return;
    cv.setPointerCapture(e.pointerId); e.preventDefault();
    const v = S.unprojectExt(n, proj);
    if (X.tool === "circle") { dragStart = v; draft = { kind: "circle", axis: v, radius: 0 }; }
    if (X.tool === "curved") { curvedStart = extLine(n); draft = curvedFrom(curvedStart, curvedStart); }
    if (X.tool === "freehand") draft = { kind: "freehand", vertices: [v], method: proj.method, hemisphere: proj.hemisphere };
    drawAll();
  });
  on(cv, "pointermove", (e: PointerEvent) => {
    const r = cv.getBoundingClientRect(), proj = P().stereo.projection; hover = { px: e.clientX - r.left, py: e.clientY - r.top };
    if (edit) {
      const v = S.unprojectExt(clampRim(evNet(e)), proj), set = P().sets[edit.k];
      if (edit.mode === "resize" && edit.orig.kind === "circle") set.window = { ...edit.orig, radius: Math.max(1, Math.min(60, S.axialAngle(v, edit.orig.axis))) };
      else set.window = S.moveWindow(edit.orig, edit.start, v);
      edit.moved = true; update(false); return;
    }
    if (draft) {
      const nc = clampRim(evNet(e)); // a little past the rim, to wrap
      if (draft.kind === "circle" && dragStart) draft = { ...draft, radius: S.axialAngle(dragStart, S.unprojectExt(nc, proj)) };
      if (draft.kind === "curved" && curvedStart) draft = curvedFrom(curvedStart, extLine(nc));
      if (draft.kind === "freehand") {
        const last = S.projectExt(draft.vertices[draft.vertices.length - 1], proj);
        if (Math.hypot(nc.x - last.x, nc.y - last.y) * R > 5) draft.vertices.push(S.unprojectExt(nc, proj));
      }
    }
    drawAll();
  });
  on(cv, "pointerup", () => {
    if (edit) { if (!edit.moved) dropHistory(); edit = null; update(false); return; }
    if (!draft) return;
    const ok = draft.kind === "circle" ? draft.radius >= 1 :
      draft.kind === "curved" ? draft.trendWidth >= 1 && draft.plungeMax - draft.plungeMin >= 1 : draft.vertices.length >= 3;
    if (ok) { pushHistory(); addSet(P(), draft); }
    cancelDraft();
    update(false);
  });
  on(cv, "pointerleave", () => { hover = null; drawAll(); });
  document.querySelectorAll<HTMLInputElement>('input[name="tool"]').forEach((r) => on(r, "change", () => { if (r.checked) setTool(r.value as Tool); }));

  // the callback resizes the canvas inside the observed box, so it is deferred a frame to keep the observer loop clean
  let frame = 0;
  const ro = new ResizeObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(resize); });
  ro.observe($("netbox"));
  X.signal.addEventListener("abort", () => { ro.disconnect(); M.cancel("net:net"); M.cancel("net:poles"); M.cancel("net:contour"); });
  // first paint: draw the net on, then let any restored data wash in behind it
  play("net", 850, M.easeInOut);
  if (P().data.planes.length) requestAnimationFrame(() => play("poles", 800));
}
